/**
 * Quais microfones estão abertos AGORA (spec 016) — pro indicador da ficha.
 *
 * Toda captura passa por `abrirMicrofone` (a porta única), que registra aqui
 * o que abriu e pra quê (escuta, ditado, gravação). Quem consome solta o
 * microfone com `track.stop()`, que não avisa ninguém: enquanto houver
 * alguma captura registrada, um relógio curto confere o `readyState` das
 * tracks e lê os níveis da mistura. Sem captura, o relógio para.
 *
 * O reconhecedor do Chrome (motor "Chrome" da escuta) abre o microfone por
 * dentro, sem passar por aqui — mas o medidor da escuta (`MicLevelGraph`)
 * abre o mesmo microfone padrão pela porta única, então ele aparece igual.
 */

import { useSyncExternalStore } from 'react';
import type { Falante } from './falante';
import type { Mistura } from './mixer';

export type UsoMic = 'escuta' | 'ditado' | 'gravacao';

export interface FonteRotulo {
  chave: string;
  /** Curto, pra tela: onde fica ("Lapela") ou o nome do aparelho. */
  rotulo: string;
  /** Nome que o Windows dá ao aparelho. */
  nome: string;
}

interface Sessao {
  stream: MediaStream;
  uso: UsoMic;
  /** Null = microfone padrão do Windows (sem mistura). */
  mistura: Mistura | null;
  fontes: FonteRotulo[];
  /** Escolhidos que não abriram nesta captura. */
  falhas: FonteRotulo[];
}

export type EstadoMic = 'falando' | 'quieto' | 'aberto' | 'caiu';

export interface MicAberto extends FonteRotulo {
  usos: UsoMic[];
  /**
   * falando: passou do corte de ruído (está somando no ditado);
   * quieto: aberto, abaixo do corte; aberto: microfone padrão (sem medida);
   * caiu: escolhido em Configurações mas não abriu ou parou de entregar som.
   */
  estado: EstadoMic;
  /** 0–1, só na mistura. */
  nivel: number;
}

export const CHAVE_PADRAO = 'padrao';
const TICK_MS = 250;

const sessoes = new Set<Sessao>();
const listeners = new Set<() => void>();
let snap: MicAberto[] = [];
let snapKey = '';
let timer: ReturnType<typeof setInterval> | null = null;

const viva = (s: Sessao) => s.stream.getAudioTracks().some((t) => t.readyState === 'live');

/** Rótulo curto do microfone padrão a partir do nome que o Chrome dá à track. */
export function rotuloPadrao(label: string): string {
  const nome = label
    .replace(/^(Padrão|Default|Comunicações|Communications)\s*-\s*/i, '')
    .replace(/^Microfone\s*\((.*)\)\s*$/i, '$1')
    .trim();
  return nome || 'Microfone padrão';
}

function calcular(): MicAberto[] {
  const porChave = new Map<string, MicAberto>();
  const ordem: EstadoMic[] = ['caiu', 'aberto', 'quieto', 'falando'];
  const juntar = (m: MicAberto) => {
    const ja = porChave.get(m.chave);
    if (!ja) return void porChave.set(m.chave, m);
    for (const u of m.usos) if (!ja.usos.includes(u)) ja.usos.push(u);
    // Duas capturas do mesmo aparelho: vale a que está melhor.
    if (ordem.indexOf(m.estado) > ordem.indexOf(ja.estado)) ja.estado = m.estado;
    ja.nivel = Math.max(ja.nivel, m.nivel);
  };
  for (const s of sessoes) {
    if (!s.mistura) {
      for (const f of s.fontes) juntar({ ...f, usos: [s.uso], estado: 'aberto', nivel: 0 });
    } else {
      const niveis = s.mistura.niveis();
      for (const f of s.fontes) {
        const n = niveis[f.chave];
        const estado: EstadoMic = !n || !n.vivo ? 'caiu' : n.aberto ? 'falando' : 'quieto';
        juntar({ ...f, usos: [s.uso], estado, nivel: n?.vivo ? n.nivel : 0 });
      }
    }
    for (const f of s.falhas) juntar({ ...f, usos: [s.uso], estado: 'caiu', nivel: 0 });
  }
  return [...porChave.values()];
}

function tick(): void {
  for (const s of sessoes) if (!viva(s)) sessoes.delete(s);
  const next = calcular();
  // Nível em 6 degraus: o indicador não precisa re-renderizar a cada sopro.
  const key = JSON.stringify(next.map((m) => [m.chave, m.rotulo, m.usos, m.estado, Math.min(5, Math.round(m.nivel * 40))]));
  if (key !== snapKey) {
    snapKey = key;
    snap = next;
    listeners.forEach((l) => l());
  }
  if (sessoes.size === 0 && timer) {
    clearInterval(timer);
    timer = null;
  }
}

/** Chamado pela porta única logo depois de abrir. */
export function registrarCaptura(s: Sessao): void {
  sessoes.add(s);
  tick();
  if (!timer && typeof setInterval !== 'undefined') timer = setInterval(tick, TICK_MS);
}

/**
 * Quem fala agora na captura deste stream (fase 2). `undefined` = esta
 * captura não separa (microfone padrão, sem lapela "Médico", ou só ela).
 */
export function falanteDaCaptura(stream: MediaStream): Falante | null | undefined {
  for (const s of sessoes) if (s.stream === stream) return s.mistura?.separaFalante ? s.mistura.falante() : undefined;
  return undefined;
}

/** Quem fala agora em alguma captura aberta pra `uso` que separa; `undefined` = nenhuma separa. */
export function falanteAgora(uso: UsoMic): Falante | null | undefined {
  for (const s of sessoes) {
    if (s.uso === uso && s.mistura?.separaFalante && viva(s)) return s.mistura.falante();
  }
  return undefined;
}

/** Pros testes. */
export function getMicrofonesAbertos(): MicAberto[] {
  tick();
  return snap;
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

const VAZIO: MicAberto[] = [];

export function useMicrofonesAbertos(): MicAberto[] {
  return useSyncExternalStore(subscribe, () => snap, () => VAZIO);
}
